import { formatDate, formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, Tenant, TenantDetail, TenantRoutingDiagnostics, CreateTenantRequest, PlatformUser, PlatformInviteRequest } from '../../services/adminApi';
import { createDefaultBannerScope, loadPortalBannerConfig, savePortalBannerConfig, type PortalBannerConfig } from '../../shared/portalBannerStore';

const INGESTION_API_KEY_HEADER = import.meta.env.VITE_INGESTION_API_KEY_HEADER || 'X-Zurvit-API-Key';
const FALLBACK_PLAN_OPTIONS = ['Starter', 'Professional', 'Enterprise'];

export const TenantsManagement: React.FC = () => {
  useDateTimePreferences();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantDetail | null>(null);
  const [pagination, setPagination] = useState({ skip: 0, take: 20 });
  const [planOptions, setPlanOptions] = useState<string[]>(FALLBACK_PLAN_OPTIONS);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getTenants(pagination);
      setTenants(Array.isArray(result?.tenants) ? result.tenants : []);
      setTotal(result?.total ?? 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    const loadPlanOptions = async () => {
      try {
        const plans = await adminApi.getSubscriptionPlans();
        const dynamic = plans
          .filter((plan) => plan.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.planName.localeCompare(b.planName))
          .map((plan) => (plan.planName || '').trim())
          .filter((name) => Boolean(name));
        if (dynamic.length > 0) {
          setPlanOptions(dynamic);
        }
      } catch {
        setPlanOptions(FALLBACK_PLAN_OPTIONS);
      }
    };

    loadPlanOptions();
  }, []);

  const handleCreateTenant = async (data: CreateTenantRequest) => {
    try {
      await adminApi.createTenant(data);
      setShowCreateModal(false);
      loadTenants();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSuspendTenant = async (tenantId: string) => {
    if (!confirm('Are you sure you want to suspend this tenant?')) return;
    try {
      await adminApi.suspendTenant(tenantId);
      loadTenants();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleReactivateTenant = async (tenantId: string) => {
    try {
      await adminApi.reactivateTenant(tenantId);
      loadTenants();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) return;
    try {
      await adminApi.deleteTenant(tenantId);
      setSelectedTenant(null);
      loadTenants();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleViewTenant = async (tenant: Tenant) => {
    try {
      const detail = await adminApi.getTenant(tenant.tenantId);
      setSelectedTenant(detail);
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>Tenant Management</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create Tenant
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading tenants...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="table-info">
            Showing {tenants.length} of {total} tenants
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Region</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Users</th>
                <th>Created</th>
                <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.tenantId}>
                  <td>
                    <button className="link-button" onClick={() => handleViewTenant(tenant)}>
                      {tenant.name}
                    </button>
                  </td>
                  <td>{tenant.region}</td>
                  <td><span className="plan-badge">{tenant.plan}</span></td>
                  <td>
                    <span className={`status-badge ${tenant.status.toLowerCase()}`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td>{tenant.userCount}</td>
                  <td>{formatDate(tenant.createdAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="tenants-actions-row">
                      <button className="btn-icon" title="View" onClick={() => handleViewTenant(tenant)}>👁️</button>
                      {tenant.slug !== 'system' && (
                        <>
                          {tenant.status === 'Active' ? (
                            <button className="btn-icon" title="Suspend" onClick={() => handleSuspendTenant(tenant.tenantId)}>⏸️</button>
                          ) : (
                            <button className="btn-icon" title="Reactivate" onClick={() => handleReactivateTenant(tenant.tenantId)}>▶️</button>
                          )}
                          <button className="btn-icon" title="Delete" onClick={() => handleDeleteTenant(tenant.tenantId)}>🗑️</button>
                        </>
                      )}
                      {tenant.slug === 'system' && (
                        <span className="status-badge active" title="Platform tenant — cannot be deleted or suspended">Platform</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-row">No tenants found</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="pagination">
            <button 
              disabled={pagination.skip === 0} 
              onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.take) }))}
            >
              Previous
            </button>
            <span>Page {Math.floor(pagination.skip / pagination.take) + 1}</span>
            <button 
              disabled={pagination.skip + pagination.take >= total} 
              onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.take }))}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateTenantModal
          planOptions={planOptions}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTenant}
        />
      )}

      {selectedTenant && (
        <TenantDetailModal
          tenant={selectedTenant}
          planOptions={planOptions}
          onClose={() => setSelectedTenant(null)}
          onRefresh={() => {
            handleViewTenant(selectedTenant);
            loadTenants();
          }}
        />
      )}
    </div>
  );
};

// =====================================================================
// Create Tenant Modal
// =====================================================================
const CreateTenantModal: React.FC<{
  planOptions: string[];
  onClose: () => void;
  onCreate: (data: CreateTenantRequest) => Promise<void>;
}> = ({ planOptions, onClose, onCreate }) => {
  const resolvedPlanOptions = planOptions.length > 0 ? planOptions : FALLBACK_PLAN_OPTIONS;
  const [formData, setFormData] = useState<CreateTenantRequest>({
    name: '',
    slug: '',
    region: 'Australia East',
    plan: resolvedPlanOptions[0] || 'Starter',
    retentionDays: 30,
    authType: 'MagicLink',
    adminEmail: '',
    adminName: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugSuggestions, setSlugSuggestions] = useState<string[]>([]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name)
    }));
    setError(null);
    setSlugSuggestions([]);
  };

  const handleSlugChange = (slug: string) => {
    setFormData(prev => ({ ...prev, slug }));
    setError(null);
    setSlugSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(formData);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.suggestions) {
        setSlugSuggestions(errorData.suggestions);
        setError(errorData.error);
      } else {
        setError(err.message || 'Failed to create tenant');
      }
      setSubmitting(false);
    }
  };

  const applySuggestion = (slug: string) => {
    setFormData(prev => ({ ...prev, slug }));
    setSlugSuggestions([]);
    setError(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <h3>Create New Tenant</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Tenant Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="e.g., Acme Corporation"
            />
          </div>

          <div className="form-group">
            <label>Tenant Slug *</label>
            <div className="slug-input-group">
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleSlugChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                required
                placeholder="e.g., acme-corp"
                pattern="[a-z0-9-]+"
                minLength={3}
                maxLength={50}
              />
              <span className="slug-preview">.zurvit.io</span>
            </div>
            <p className="help-text">
              Used in URLs: <code>https://.../{formData.slug || 'slug'}/dashboard</code>
            </p>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
              {slugSuggestions.length > 0 && (
                <div className="slug-suggestions">
                  <p>Available alternatives:</p>
                  <div className="suggestion-buttons">
                    {slugSuggestions.map(suggestion => (
                      <button
                        key={suggestion}
                        type="button"
                        className="suggestion-btn"
                        onClick={() => applySuggestion(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Region</label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              >
                <option value="Australia East">Australia East</option>
                <option value="Australia Southeast">Australia Southeast</option>
                <option value="East US">East US</option>
                <option value="West US">West US</option>
                <option value="West Europe">West Europe</option>
              </select>
            </div>

            <div className="form-group">
              <label>Plan</label>
                <select
                  value={formData.plan}
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                >
                  {resolvedPlanOptions.map((plan) => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>
            </div>

          <div className="form-row">
            <div className="form-group">
              <label>Retention Days</label>
              <input
                type="number"
                value={formData.retentionDays}
                onChange={(e) => setFormData({ ...formData, retentionDays: parseInt(e.target.value) })}
                min={7}
                max={365}
              />
            </div>

            <div className="form-group">
              <label>Auth Type</label>
              <select
                value={formData.authType}
                onChange={(e) => setFormData({ ...formData, authType: e.target.value })}
              >
                <option value="MagicLink">Magic Link</option>
                <option value="EntraID">Entra ID (SSO)</option>
                <option value="SAML">SAML</option>
              </select>
            </div>
          </div>

          <hr />

          <h4>Admin User</h4>
          <div className="form-group">
            <label>Admin Email *</label>
            <input
              type="email"
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              required
              placeholder="admin@company.com"
            />
          </div>

          <div className="form-group">
            <label>Admin Name</label>
            <input
              type="text"
              value={formData.adminName}
              onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
              placeholder="John Smith"
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =====================================================================
// Tenant Detail Modal
// =====================================================================
const TenantDetailModal: React.FC<{
  tenant: TenantDetail;
  planOptions: string[];
  onClose: () => void;
  onRefresh: () => void;
}> = ({ tenant, planOptions, onClose, onRefresh }) => {
  useDateTimePreferences();
  const resolvedPlanOptions = planOptions.length > 0 ? planOptions : FALLBACK_PLAN_OPTIONS;
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editData, setEditData] = useState({
    name: tenant.name,
    region: tenant.region,
    plan: tenant.plan,
    retentionDays: tenant.retentionDays,
  });
  const [bannerConfig, setBannerConfig] = useState<PortalBannerConfig>(() => loadPortalBannerConfig());
  const [tenantBannerEnabled, setTenantBannerEnabled] = useState(false);
  const [tenantBannerText, setTenantBannerText] = useState('');
  const [tenantBannerSaving, setTenantBannerSaving] = useState(false);
  const [routingAliasInput, setRoutingAliasInput] = useState(tenant.slug);
  const [routingDiagnostics, setRoutingDiagnostics] = useState<TenantRoutingDiagnostics | null>(null);
  const [routingDiagnosticsLoading, setRoutingDiagnosticsLoading] = useState(false);
  const [routingDiagnosticsError, setRoutingDiagnosticsError] = useState<string | null>(null);

  const tenantBannerScope = bannerConfig.tenants[tenant.slug.toLowerCase()] || createDefaultBannerScope('Tenant Notice');

  useEffect(() => {
    const current = loadPortalBannerConfig();
    setBannerConfig(current);
    const tenantScope = current.tenants[tenant.slug.toLowerCase()] || createDefaultBannerScope('Tenant Notice');
    setTenantBannerEnabled(tenantScope.enabled);
    setTenantBannerText(tenantScope.text || '');
  }, [tenant.slug]);

  useEffect(() => {
    setRoutingAliasInput(tenant.slug);
    setRoutingDiagnostics(null);
    setRoutingDiagnosticsError(null);
  }, [tenant.tenantId, tenant.slug]);

  const handleRegenerateKey = async () => {
    if (!confirm('This will invalidate the current API key. Continue?')) return;
    setRegeneratingKey(true);
    try {
      await adminApi.regenerateApiKey(tenant.tenantId, '00000000-0000-0000-0000-000000000000');
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setRegeneratingKey(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard`);
  };

  const handleRunRoutingDiagnostics = async () => {
    const alias = routingAliasInput.trim();
    if (!alias) {
      setRoutingDiagnosticsError('Alias is required');
      return;
    }

    setRoutingDiagnosticsLoading(true);
    setRoutingDiagnosticsError(null);
    try {
      const diagnostics = await adminApi.getTenantRoutingDiagnostics(alias);
      setRoutingDiagnostics(diagnostics);
    } catch (err: any) {
      setRoutingDiagnosticsError(err?.message || 'Failed to load routing diagnostics');
      setRoutingDiagnostics(null);
    } finally {
      setRoutingDiagnosticsLoading(false);
    }
  };

  const handleStartEdit = () => {
    setEditData({
      name: tenant.name,
      region: tenant.region,
      plan: tenant.plan,
      retentionDays: tenant.retentionDays,
    });
    setEditError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editData.name.trim()) {
      setEditError('Tenant name is required');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await adminApi.updateTenant(tenant.tenantId, {
        name: editData.name,
        region: editData.region,
        plan: editData.plan,
        retentionDays: editData.retentionDays,
      });
      setIsEditing(false);
      onRefresh();
    } catch (err: any) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTenantBanner = () => {
    setTenantBannerSaving(true);
    try {
      const next: PortalBannerConfig = {
        ...bannerConfig,
        tenants: {
          ...bannerConfig.tenants,
          [tenant.slug.toLowerCase()]: {
            ...tenantBannerScope,
            enabled: tenantBannerEnabled,
            text: tenantBannerText.trim(),
            title: tenantBannerScope.title || `${tenant.name} Notice`,
            variant: tenantBannerScope.variant || 'info',
            dismissible: tenantBannerScope.dismissible !== false,
          },
          [tenant.tenantId.toLowerCase()]: {
            ...tenantBannerScope,
            enabled: tenantBannerEnabled,
            text: tenantBannerText.trim(),
            title: tenantBannerScope.title || `${tenant.name} Notice`,
            variant: tenantBannerScope.variant || 'info',
            dismissible: tenantBannerScope.dismissible !== false,
          },
        },
      };
      savePortalBannerConfig(next);
      setBannerConfig(next);
      alert('Tenant banner settings saved');
    } finally {
      setTenantBannerSaving(false);
    }
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-xl tenant-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title-row">
          <h3>{isEditing ? 'Edit Tenant' : tenant.name}</h3>
          {!isEditing && (
            <button className="btn-secondary btn-sm" onClick={handleStartEdit}>
              Edit
            </button>
          )}
        </div>

        {editError && <div className="error-message">{editError}</div>}

        <div className="tenant-detail-grid">
          <div className="detail-card">
            <h4>General</h4>
            <dl>
              <dt>Status</dt>
              <dd><span className={`status-badge ${tenant.status.toLowerCase()}`}>{tenant.status}</span></dd>

              <dt>Name</dt>
              <dd>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-edit"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    required
                  />
                ) : (
                  tenant.name
                )}
              </dd>

              <dt>Plan</dt>
              <dd>
                {isEditing ? (
                  <select
                    className="inline-edit"
                    value={editData.plan}
                    onChange={(e) => setEditData({ ...editData, plan: e.target.value })}
                  >
                    {resolvedPlanOptions.map((plan) => (
                      <option key={plan} value={plan}>{plan}</option>
                    ))}
                  </select>
                ) : (
                  tenant.plan
                )}
              </dd>

              <dt>Subscription State</dt>
              <dd>{tenant.subscriptionState || '-'}</dd>

              <dt>Billing Period</dt>
              <dd>{tenant.subscriptionBillingPeriod || '-'}</dd>

              <dt>Plan Days Left</dt>
              <dd>{tenant.planDaysLeft != null ? String(tenant.planDaysLeft) : '-'}</dd>

              <dt>Plan Expiry</dt>
              <dd>{tenant.planExpiry ? formatDateTime(tenant.planExpiry, { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</dd>

              <dt>Client Initial Signup Date</dt>
              <dd>{tenant.clientInitialSignupAt ? formatDateTime(tenant.clientInitialSignupAt, { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</dd>

              <dt>Region</dt>
              <dd>
                {isEditing ? (
                  <select
                    className="inline-edit"
                    value={editData.region}
                    onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                  >
                    <option value="Australia East">Australia East</option>
                    <option value="Australia Southeast">Australia Southeast</option>
                    <option value="East US">East US</option>
                    <option value="West US">West US</option>
                    <option value="West Europe">West Europe</option>
                  </select>
                ) : (
                  tenant.region
                )}
              </dd>

              <dt>Retention</dt>
              <dd>
                {isEditing ? (
                  <div className="inline-edit-group">
                    <input
                      type="number"
                      className="inline-edit inline-edit-narrow"
                      value={editData.retentionDays}
                      onChange={(e) => setEditData({ ...editData, retentionDays: parseInt(e.target.value) || 30 })}
                      min={7}
                      max={365}
                    />
                    <span>days</span>
                  </div>
                ) : (
                  `${tenant.retentionDays} days`
                )}
              </dd>

              <dt>Auth Type</dt>
              <dd>{tenant.authType}</dd>

              <dt>Slug</dt>
              <dd><code>{tenant.slug}</code></dd>

              <dt>Created</dt>
              <dd>{formatDateTime(tenant.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</dd>
            </dl>
          </div>

          <div className="detail-card">
            <h4>Usage (30 days)</h4>
            {tenant.usage ? (
              <dl>
                <dt>Alerts/Day (avg)</dt>
                <dd>{tenant.usage.alertsPerDay.toFixed(1)}</dd>
                <dt>Total Alerts</dt>
                <dd>{tenant.usage.totalAlerts}</dd>
                <dt>SMS Sent</dt>
                <dd>{tenant.usage.smsCount}</dd>
                <dt>Email Sent</dt>
                <dd>{tenant.usage.emailCount}</dd>
                <dt>Integrations</dt>
                <dd>{tenant.usage.integrations}</dd>
                <dt>Active Schedules</dt>
                <dd>{tenant.usage.activeSchedules}</dd>
              </dl>
            ) : (
              <p className="loading">Loading...</p>
            )}
          </div>

          <div className="detail-card">
            <h4>API Access</h4>
            <dl>
              <dt>API Key</dt>
              <dd className="api-key-cell">
                {tenant.apiKey ? (
                  <>
                    <code>{tenant.apiKey.substring(0, 20)}...</code>
                    <button className="btn-icon" onClick={() => copyToClipboard(tenant.apiKey!, 'API Key')} title="Copy">📋</button>
                    <button className="btn-icon" onClick={handleRegenerateKey} disabled={regeneratingKey} title="Regenerate">🔄</button>
                  </>
                ) : (
                  <span className="loading">Loading...</span>
                )}
              </dd>
              <dt>Ingestion URL</dt>
              <dd>
                <code>https://zurvit-func.azurewebsites.net/api/v1/ingest</code>
                <span style={{ fontSize: '0.85em', color: 'var(--color-text-secondary)', marginLeft: '8px' }}>(API key in {INGESTION_API_KEY_HEADER} header)</span>
              </dd>
            </dl>
          </div>

          <div className="detail-card">
            <h4>Integrations</h4>
            {tenant.usage ? (
              <dl>
                <dt>Total</dt>
                <dd>{tenant.usage.integrations}</dd>
                <dt>Inbound</dt>
                <dd><span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Breakdown coming soon</span></dd>
                <dt>Outbound</dt>
                <dd><span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Breakdown coming soon</span></dd>
              </dl>
            ) : (
              <p className="loading">Loading...</p>
            )}
          </div>

          <div className="detail-card">
            <h4>Tenant Banner</h4>
            <p className="help-text">Controls the banner for this tenant only.</p>
            <div className="banner-setting-row">
              <label className="toggle-switch" title="Enable or disable tenant banner">
                <input
                  type="checkbox"
                  checked={tenantBannerEnabled}
                  onChange={(e) => setTenantBannerEnabled(e.target.checked)}
                />
                <span className="slider" />
              </label>
              <span>{tenantBannerEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <textarea
              className="inline-edit"
              rows={3}
              value={tenantBannerText}
              onChange={(e) => setTenantBannerText(e.target.value)}
              placeholder="Tenant-specific notice text"
            />
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button className="btn-primary btn-sm" onClick={handleSaveTenantBanner} disabled={tenantBannerSaving}>
                {tenantBannerSaving ? 'Saving...' : 'Save Tenant Banner'}
              </button>
            </div>
          </div>

          <div className="detail-card">
            <h4>Routing Diagnostics</h4>
            <p className="help-text">Resolve and inspect alias routing for this tenant.</p>
            <div className="routing-diagnostics-controls">
              <input
                type="text"
                className="inline-edit"
                value={routingAliasInput}
                onChange={(e) => setRoutingAliasInput(e.target.value)}
                placeholder="Alias (slug, subdomain, custom domain)"
              />
              <button
                className="btn-primary btn-sm"
                onClick={handleRunRoutingDiagnostics}
                disabled={routingDiagnosticsLoading}
              >
                {routingDiagnosticsLoading ? 'Checking...' : 'Run Diagnostics'}
              </button>
            </div>

            {routingDiagnosticsError && <div className="error-message">{routingDiagnosticsError}</div>}

            {routingDiagnostics && (
              <div className="routing-diagnostics-results">
                <dl>
                  <dt>Alias Input</dt>
                  <dd><code>{routingDiagnostics.aliasInput || '-'}</code></dd>
                  <dt>Alias Normalized</dt>
                  <dd><code>{routingDiagnostics.aliasNormalized || '-'}</code></dd>
                  <dt>Resolved</dt>
                  <dd>
                    <span className={`status-badge ${routingDiagnostics.resolution?.resolved ? 'active' : 'inactive'}`}>
                      {routingDiagnostics.resolution?.resolved ? 'Yes' : 'No'}
                    </span>
                  </dd>
                  <dt>Alias Type</dt>
                  <dd>{routingDiagnostics.resolution?.aliasType || '-'}</dd>
                  <dt>Routing Version</dt>
                  <dd>{routingDiagnostics.routingVersion || '-'}</dd>
                  <dt>Deployment</dt>
                  <dd>{routingDiagnostics.deploymentId || '-'}</dd>
                  <dt>Counts</dt>
                  <dd>
                    <span className="chip">CustomDomain: {routingDiagnostics.counts?.customDomain ?? 0}</span>{' '}
                    <span className="chip">Subdomain: {routingDiagnostics.counts?.subdomain ?? 0}</span>{' '}
                    <span className="chip">Slug: {routingDiagnostics.counts?.slug ?? 0}</span>
                  </dd>
                </dl>

                <div className="routing-diagnostics-match-list">
                  <strong>Matches</strong>
                  {Array.isArray(routingDiagnostics.matches) && routingDiagnostics.matches.length > 0 ? (
                    <ul>
                      {routingDiagnostics.matches.map((match, idx) => (
                        <li key={`${match.source}-${match.tenantId}-${idx}`}>
                          <code>{match.source}</code> {'->'} {match.tenantName} (<code>{match.tenantSlug}</code>, {match.region})
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="help-text">No matches found.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <TenantUsersSection tenantId={tenant.tenantId} />
        </div>

        <div className="form-actions">
          {isEditing ? (
            <>
              <button onClick={handleCancelEdit} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn-secondary">Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Tenant Users Section
// =====================================================================
const TenantUsersSection: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState<PlatformInviteRequest>({ email: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await adminApi.getTenantUsers(tenantId, { search: search || undefined, take: 100 });
      setUsers(Array.isArray(result?.users) ? result.users : []);
      setTotal(typeof result?.total === 'number' ? result.total : 0);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInvite = async () => {
    if (!inviteData.email.trim()) {
      setInviteError('Email is required');
      return;
    }
    setInviting(true);
    setInviteError(null);
    try {
      await adminApi.inviteTenantUser(tenantId, inviteData);
      setShowInvite(false);
      setInviteData({ email: '' });
      await loadUsers();
    } catch (err: any) {
      setInviteError(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateUser = async (userId: string) => {
    setActionError(null);
    try {
      await adminApi.updateTenantUser(tenantId, userId, {
        role: editRole || undefined,
        status: editStatus || undefined,
      });
      setEditingUserId(null);
      await loadUsers();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Remove user ${email} from this tenant? This cannot be undone.`)) return;
    setActionError(null);
    try {
      await adminApi.deleteTenantUser(tenantId, userId);
    } catch (err: any) {
      setActionError(err.message);
    }
    await loadUsers();
  };

  const startEditing = (user: PlatformUser) => {
    setEditingUserId(user.userId);
    setEditRole(user.roles?.[0] || '');
    setEditStatus(user.status);
    setActionError(null);
  };

  const ROLES = ['Viewer', 'Responder', 'TeamLead', 'TenantAdmin'];
  const ROLE_LABELS: Record<string, string> = { Viewer: 'Viewer', Responder: 'Responder', TeamLead: 'Team Leader', TenantAdmin: 'Tenant Admin' };
  const STATUSES = ['Active', 'Inactive', 'PendingInvitation', 'Suspended'];

  return (
    <div className="detail-card users-section">
      <div className="users-header">
        <h4>Users ({total})</h4>
        <div className="users-actions">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="users-search"
          />
          <button className="btn-sm btn-primary" onClick={() => { setShowInvite(true); setInviteError(null); }}>
            + Invite User
          </button>
        </div>
      </div>

      {actionError && <div className="error-message">{actionError}</div>}

      {showInvite && (
        <div className="invite-form">
          <h5>Invite New User</h5>
          {inviteError && <div className="error-message">{inviteError}</div>}
          <div className="invite-fields">
            <input
              type="email"
              placeholder="Email address *"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              className="inline-edit"
            />
            <input
              type="text"
              placeholder="Display name"
              value={inviteData.displayName || ''}
              onChange={(e) => setInviteData({ ...inviteData, displayName: e.target.value })}
              className="inline-edit"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={inviteData.phone || ''}
              onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
              className="inline-edit-narrow"
            />
            <select
              value={inviteData.role || ''}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              className="inline-edit-narrow"
            >
              <option value="">Role...</option>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>
          <div className="invite-actions">
            <button className="btn-sm btn-secondary" onClick={() => setShowInvite(false)} disabled={inviting}>Cancel</button>
            <button className="btn-sm btn-primary" onClick={handleInvite} disabled={inviting}>
              {inviting ? 'Inviting...' : 'Send Invite'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="loading">Loading users...</p>
      ) : users.length === 0 ? (
        <p>No users found</p>
      ) : (
        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId}>
                <td>{user.displayName}</td>
                <td>{user.email}</td>
                <td>
                  {editingUserId === user.userId ? (
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className="inline-edit-narrow"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                  ) : (
                    <span className="role-badge">{ROLE_LABELS[user.roles?.[0]] || user.roles?.[0] || 'None'}</span>
                  )}
                </td>
                <td>
                  {editingUserId === user.userId ? (
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="inline-edit-narrow"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span className={`status-badge ${user.status.toLowerCase().replace(/\s+/g, '')}`}>{user.status}</span>
                  )}
                </td>
                <td className="user-actions-cell">
                  {editingUserId === user.userId ? (
                    <>
                      <button className="btn-sm btn-primary" onClick={() => handleUpdateUser(user.userId)}>Save</button>
                      <button className="btn-sm btn-secondary" onClick={() => setEditingUserId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-sm" onClick={() => startEditing(user)} title="Edit">Edit</button>
                      <button className="btn-sm btn-danger" onClick={() => handleDeleteUser(user.userId, user.email)} title="Remove">Remove</button>
                    </>
                  )}
                </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
